/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTheme } from '@mui/material/styles';
import {
	DataGridProProps,
	GridColDef,
	GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { ReactNode } from 'react';

import Variables from 'library/utils/themes/Variables';

import { LoadingDataWrapper } from '../loaders/LoadingDataWrapper';
import { LoadingWrapperProps } from '../loaders/LoadingWrapper';
import { LocalizedDataGrid } from './LocalizedDataGrid';

export type VolvoDataGridProps<R extends GridValidRowModel = any> = Omit<
	DataGridProProps<R> & React.RefAttributes<HTMLDivElement>,
	'rows' | 'isLoading'
> & {
	rows: DataGridProProps<R>['rows'] | null | undefined;
	isLoading: LoadingDataWrapper<R>['loading'];
	isInnerGrid?: boolean;
	loadingWrapperProps?: Omit<LoadingWrapperProps, 'loading'>;
	noDataComponent?: ReactNode;
};

export const VolvoDataGrid = <R extends GridValidRowModel = any>({
	rows,
	isLoading,
	isInnerGrid = false,
	loadingWrapperProps,
	noDataComponent,
	sx = [],
	...props
}: VolvoDataGridProps<R>) => {
	const theme = useTheme<Variables>();
	const shouldAutoHeight = props.autoHeight === true;
	//Sonar qube need to have it in 2 separeta conditions
	// test structure cannot allow to be it outside function

	const isInnerStyleChoice = isInnerGrid ? innerDataGridStyle : null;

	let height: string | number;

	if (shouldAutoHeight) {
		height = 'auto';
	} else if (!isInnerGrid) {
		height = theme.custom.dataGridHeight;
	} else {
		height = 'unset';
	}

	return (
		<LoadingDataWrapper
			data={rows}
			loading={isLoading}
			noDataComponent={noDataComponent}
			slotProps={{
				dataGridWrapper: {
					display: 'flex',
					flexDirection: 'column',
					height: height,
					maxHeight: isInnerGrid && !shouldAutoHeight ? 450 : 'unset',
					minHeight: 100,
				},
				noDataWrapper: {
					sx: isInnerGrid ? innerDatGridNoDataBoxStyle : null,
				},
				loadingWrapper: loadingWrapperProps,
			}}
			render={(data) => (
				<LocalizedDataGrid
					rows={data}
					{...props}
					sx={[
						isInnerStyleChoice,
						...(Array.isArray(sx) ? sx : [sx]),
					]}
				/>
			)}
		/>
	);
};

export type TypeSafeColDef<R extends GridValidRowModel = any> =
	GridColDef<R> & { field: keyof R | 'actions' };

const innerDataGridStyle = {
	// any padding and margin horizontal breaks scrolling
	my: 0,
	py: 0,
	mx: 4,
	'& .MuiDataGrid-columnHeaders': {
		borderTopColor: 'transparent',
	},
};

const innerDatGridNoDataBoxStyle = {
	height: 100,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
};
